import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  MIN_LOT_IMAGE_LONG_EDGE,
  MIN_LOT_IMAGE_SHORT_EDGE,
  prepareLotImage,
  type PreparedLotImage,
} from '@/lib/parkingLot/lotImage';

interface ParkingLotImageUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  onUploadComplete: (imageId: string) => void;
}

export function ParkingLotImageUploadDialog({
  open,
  onOpenChange,
  shopId,
  onUploadComplete,
}: ParkingLotImageUploadDialogProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  // NOV4 P2: what will be stored — the chosen file, or its optimized copy (resized / converted to JPEG).
  const [prepared, setPrepared] = useState<PreparedLotImage | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { toast } = useToast();

  const handleFile = useCallback(async (file: File) => {
    setError(null);
    setIsPreparing(true);
    try {
      const ready = await prepareLotImage(file);
      setFile(file);
      setPrepared(ready);
      setDimensions({ width: ready.width, height: ready.height });
      setPreview(URL.createObjectURL(ready.blob));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setFile(null);
      setPrepared(null);
      setPreview(null);
      setDimensions(null);
    } finally {
      setIsPreparing(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFile(droppedFile);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  }, [handleFile]);

  const handleUpload = async () => {
    if (!file || !prepared || !dimensions) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Generate unique filename
      const ext = prepared.extension;
      const uuid = crypto.randomUUID();
      const storagePath = `${shopId}/${uuid}.${ext}`;

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 80));
      }, 200);

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('parking-lot-images')
        .upload(storagePath, prepared.blob, {
          cacheControl: '3600',
          contentType: prepared.contentType,
          upsert: false,
        });

      clearInterval(progressInterval);

      if (uploadError) throw uploadError;

      setUploadProgress(90);

      // Create database record
      const { data: imageRecord, error: dbError } = await supabase
        .from('parking_lot_images')
        .insert({
          shop_id: shopId,
          image_path: storagePath,
          image_width: dimensions.width,
          image_height: dimensions.height,
          status: 'PENDING',
        })
        .select('id')
        .single();

      if (dbError) throw dbError;

      setUploadProgress(100);

      toast({
        title: 'Image uploaded',
        description: 'Now calibrate the image to set the scale.',
      });

      onUploadComplete(imageRecord.id);
      handleReset();
      onOpenChange(false);

    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image.');
      toast({
        title: 'Upload failed',
        description: err.message || 'Failed to upload image.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPrepared(null);
    setPreview(null);
    setDimensions(null);
    setError(null);
    setUploadProgress(0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Parking Lot Image</DialogTitle>
          <DialogDescription>
            Upload an overhead photo of your parking lot — a drone shot, a satellite map screenshot or a photo from
            above all work. Large photos are optimized automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!file ? (
            <div
              className={cn(
                'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
                isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50',
                error && 'border-destructive'
              )}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <input
                id="file-input"
                type="file"
                accept="image/*"
                data-testid="parking-lot-image-input"
                className="hidden"
                onChange={handleFileSelect}
              />
              <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm font-medium">
                {isPreparing ? 'Preparing image…' : 'Drag and drop your image here, or click to browse'}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Any JPG, PNG or WebP photo at least {MIN_LOT_IMAGE_LONG_EDGE}×{MIN_LOT_IMAGE_SHORT_EDGE}px
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden bg-muted aspect-video">
                <img
                  src={preview!}
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={handleReset}
                  disabled={isUploading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ImageIcon className="h-4 w-4" />
                <span className="truncate flex-1">{file.name}</span>
                {dimensions && (
                  <span className="text-xs" data-testid="parking-lot-image-dimensions">
                    {dimensions.width} × {dimensions.height}{prepared?.optimized ? ' (optimized)' : ''}
                  </span>
                )}
              </div>

              {isUploading && (
                <div className="space-y-2">
                  <Progress value={uploadProgress} />
                  <p className="text-xs text-center text-muted-foreground">
                    Uploading... {uploadProgress}%
                  </p>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!file || !prepared || isUploading || isPreparing}>
            {isUploading ? 'Uploading...' : 'Upload & Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
